/**
 * UnifiedMessageCard Component - Redesigned chat response interface
 *
 * Phase 1: Foundation - Clean card-based layout replacing MessageBubble
 *
 * Design Principles:
 * - Single unified card for all response elements
 * - Progressive disclosure for advanced features
 * - Maintain existing Allianz colour scheme (#007ab3)
 * - Mobile-first responsive design
 * - No tech debt - clean replacement (not enhancement)
 *
 * Component Architecture:
 * - ResponseHeader: System branding, confidence, metrics
 * - ResponseContent: Clean markdown with numbered citations
 * - ExpandableSections: Sources and debug panels (future phases)
 */

import React from 'react';
import { Message } from '../types/chat';
import { ResponseContent } from './ResponseContent';
import { ExpandableSections } from './ExpandableSections';
import { Copy, Check, ThumbsUp, ThumbsDown, Send } from 'lucide-react';
import useAuth from '@/contexts/useAuth';
import { getStreamingEndpoint } from '@/utils/featureFlags';
import { toast } from 'sonner';

interface UnifiedMessageCardProps {
  message: Message;
  sessionId: string;
  showTimestamp?: boolean;
  showDebug?: boolean;
  messageIndex: number;
}

export const UnifiedMessageCard: React.FC<UnifiedMessageCardProps> = ({
  message,
  sessionId,
  messageIndex,
  showTimestamp = true,
  showDebug = true,
}) => {
  // Debug log to see what's being passed to the message card
  if (message.sender === 'agent') {
    console.debug('UnifiedMessageCard received agent message:', {
      hasCitations: !!message.citations?.length,
      hasDebug: !!message.debug,
      hasTraces: !!message.traces?.length,
      hasProcessingSteps: !!message.processing_steps?.length,
      hasDebugInfo: !!message.debug_info,
      hasPerformanceMetrics: !!message.performance_metrics,
      message: message,
    });
  }

  const isUser = message.sender === 'user';
  const isSystem = message.sender === 'system';
  const isThinking = message.type === 'thinking';
  const isError = message.type === 'error';
  const isStreaming = message.type === 'streaming' && message.isLoading;
  const [recentlyCopied, setRecentlyCopied] = React.useState(false);
  // Feedback UI state
  const [rating, setRating] = React.useState<number | null>(null);
  const [comment, setComment] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  // auto-resize textarea when comment changes
  React.useEffect(() => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    ta.style.height = 'auto';
    const maxHeight = 200; // px
    ta.style.height = `${Math.min(ta.scrollHeight, maxHeight)}px`;
  }, [comment]);
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
  const [submittingFeedback, setSubmittingFeedback] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const auth = useAuth();

  // Responsive check
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile(e.matches);
    update(mq);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Handle clicking the copy button which copies the message to the clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setRecentlyCopied(true);
    setTimeout(() => setRecentlyCopied(false), 2000); // Reset after 2 seconds
  };

  // Hide streaming messages (they're shown in the loading indicator instead)
  if (isStreaming) {
    return null;
  }

  // User messages maintain simple bubble design
  if (isUser) {
    return (
      <div className="flex flex-col items-end mb-4">
        <div className="max-w-[90%] rounded-lg px-4 py-3 bg-allianz-primary text-white rounded-br-sm break-words">
          {message.content}
        </div>
        {showTimestamp && (
          <div className="text-xs text-gray-500 mt-1">
            {message.timestamp.toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  }

  // System/error messages maintain existing design
  if (isSystem || isError) {
    return (
      <div className="flex flex-col items-start mb-4">
        <div className="max-w-[90%] rounded-lg px-4 py-3 bg-red-50 text-red-800 border border-red-200 rounded-bl-sm break-words">
          {message.content}
        </div>
        {showTimestamp && (
          <div className="text-xs text-gray-500 mt-1">
            {message.timestamp.toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  }

  // Thinking messages maintain existing design
  if (isThinking) {
    return (
      <div className="flex flex-col items-start mb-4">
        <div className="max-w-[90%] rounded-lg px-4 py-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-bl-sm italic break-words">
          {message.content}
        </div>
        {showTimestamp && (
          <div className="text-xs text-gray-500 mt-1">
            {message.timestamp.toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  }

  // Main agent response - Bot message in bubble
  return (
    <div className="flex flex-col items-start mb-4 w-full">
      <div className="max-w-[90%] rounded-lg rounded-bl-sm px-4 py-3 bg-teal-100/50 text-gray-900 text-left break-words select-text">
        <ResponseContent message={message} />
        <p className="text-xs italic text-gray-600 mt-3 leading-relaxed">
          This response is generated by AI for Adviser use only and should be
          verified against the Source Documents linked in the sidebar.
        </p>
      </div>
      {/* Copy Button + Rating controls + inline comment (all same parent) */}
      <div className="flex justify-start w-full max-w-[90%] mb-4 mt-2 items-center gap-3">
        {message.sender === 'agent' && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-2 min-h-10">
              <button
                type="button"
                className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-allianz-primary hover:bg-gray-100 rounded transition"
                aria-label="Copy message"
                onClick={handleCopy}
              >
                {recentlyCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                  </>
                )}
              </button>
              <button
                type="button"
                aria-pressed={rating === 1}
                aria-label="Thumbs up"
                disabled={feedbackSubmitted}
                onClick={() => {
                  if (feedbackSubmitted) return;
                  setRating((prev) => (prev === 1 ? null : 1));
                }}
                className={`p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-allianz-primary ${
                  rating === 1
                    ? 'bg-allianz-primary text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <ThumbsUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-pressed={rating === 0}
                aria-label="Thumbs down"
                disabled={feedbackSubmitted}
                onClick={() => {
                  if (feedbackSubmitted) return;
                  setRating((prev) => (prev === 0 ? null : 0));
                }}
                className={`p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-allianz-primary ${
                  rating === 0
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <ThumbsDown className="h-4 w-4" />
              </button>
            </div>
            {rating !== null && !feedbackSubmitted && (
              <div className="flex items-center gap-2 ml-3 flex-1 min-w-0">
                <textarea
                  ref={textareaRef}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    rating === 1
                      ? isMobile
                        ? 'Why?'
                        : 'What did you like? (optional)'
                      : isMobile
                        ? 'Improvements?'
                        : 'What could be improved? (optional)'
                  }
                  className="flex-1 min-w-0 rounded-md border border-gray-200 p-2 text-sm focus:ring-1 focus:ring-allianz-primary resize-none overflow-auto"
                  aria-label="Feedback comment"
                  autoFocus
                  rows={1}
                />
                <button
                  type="button"
                  onClick={async () => {
                    setSubmittingFeedback(true);
                    try {
                      const endpoint = getStreamingEndpoint();
                      if (!endpoint) {
                        toast.error(
                          'Could not save feedback: no API endpoint configured',
                        );
                        return;
                      }
                      const pk =
                        auth.userProfile?.email || auth.user?.username || '';
                      try {
                        const res = await fetch(`${endpoint}/feedback`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            user_id: pk,
                            session_id: sessionId,
                            message_index: messageIndex,
                            feedback: { rating: rating, comment: comment },
                          }),
                        });
                        if (!res.ok) {
                          const body = await res.json().catch(() => ({}));
                          throw new Error(
                            body.error || body.detail || `HTTP ${res.status}`,
                          );
                        }
                      } catch (err) {
                        const msg =
                          err instanceof Error ? err.message : String(err);
                        console.error('Failed to persist feedback:', err);
                        toast.error(`Could not save feedback: ${msg}`);
                        return;
                      }
                      toast('Thanks for your feedback!', {
                        style: {
                          background: '#ccfbf1',
                          color: '#134e4a',
                          border: '1px solid #99f6e4',
                        },
                      });
                      await new Promise((r) => setTimeout(r, 400));
                      setFeedbackSubmitted(true);
                    } finally {
                      setSubmittingFeedback(false);
                    }
                  }}
                  disabled={submittingFeedback}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md focus:outline-none ${
                    submittingFeedback
                      ? 'opacity-60 cursor-wait'
                      : 'bg-allianz-primary text-white hover:opacity-95'
                  }`}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            )}
            {message.sender === 'agent' && feedbackSubmitted && (
              <div className="w-full max-w-[90%] mt-3 text-sm text-green-700">
                Submitted!
              </div>
            )}
          </div>
        )}
      </div>
      {/* ExpandableSections - Progressive disclosure for sources and debug */}
      <div className="w-full max-w-[90%]">
        <ExpandableSections message={message} showDebug={showDebug} />
      </div>
    </div>
  );
};
